<template>
  <div class="flex flex-col gap-4 border-b pb-8 md:pb-10" v-if="curatedMeals?.length">
    <AtomBlockHeaderSeo 
      :label="$t('city.start.events.label', { city: fat.city?.name }) + ' ' + headingSuffix"
      :heading="$t('city.start.events.heading', { city: fat.city?.name })"
    />
    <div class="-mx-[4vw]">
      <div class="w-full overflow-y-scroll no-scrollbar px-[4vw]">
        <div class="flex items-start gap-6 sm:gap-x-8">
          <div class="max-md:min-w-[18rem]" :class="{ 'md:w-full': curatedMeals.length === 1, 'md:w-1/2': curatedMeals.length === 2, 'md:w-1/3': curatedMeals.length >= 3 }" v-for="meal in curatedMeals" :key="meal.id">
            <EventsListCard
              :date="toDayjs().isAfter(meal.startDate) ? meal?.nextDates[0] : meal.startDate"
              :meal="meal"
              :restaurant="meal.restaurant"
              :style="curatedMeals.length === 1 ? 'promoted' : null"
            />
          </div>
          <div class="w-0 !opacity-0 md:hidden">&nbsp;</div>
        </div>
      </div>
    </div>
    <!-- Meals for debugging -->
    <div class="grid gap-4" v-if="route.query?.debug">
      <div class="grid gap-4">
        <div class="font-bold">Curated Events</div>
        <table class="debug-table">
          <tbody>
            <tr>
              <th class="w-12">ID</th>
              <th class="w-1/4">Restaurant</th>
              <th class="w-12">Rank</th>
              <th class="w-12">startDate</th>
              <th class="w-12">Date</th>
              <th class="w-12">Promoted</th>
              <th class="w-12">Meal ID</th>
              <th class="w-1/4">Name</th>
              <th class="w-1/4">Desc</th>
              <th class="w-12">Type</th>
              <th class="w-12">AltType</th>
            </tr>
            <tr v-for="meal in curatedMeals" :key="meal.id">
              <td v-html="meal.restaurant?.id" />
              <td v-html="meal.restaurant?.name" />
              <td v-html="meal.restaurant?.rank" />
              <td v-html="meal.startDate" />
              <td v-html="toDayjs().isAfter(meal.startDate) ? toDayjs().format('YYYY-MM-DD') : meal.startDate" />
              <td>
                <div class="whitespace-nowrap" v-if="meal.restaurant?.promoted && meal.restaurant?.promoted?.promotionTypes?.includes('events')">
                  {{ meal.restaurant?.promoted?.text }} ({{ meal.restaurant?.promoted?.promotionTypes.join(',') }}, {{ meal.restaurant?.promoted?.rank }})
                  <br>
                  {{ meal.restaurant?.promoted?.startDate }} {{ meal.restaurant?.promoted?.endDate }}
                </div>
              </td>
              <td v-html="meal.id" />
              <td v-html="meal.name" />
              <td v-html="meal.description?.length > 10 ? meal.description.slice(0, 8) + '...' : meal.description" />
              <td v-html="meal.type" />
              <td v-html="meal.altType" />
            </tr>
          </tbody>
        </table>
      </div>
      <div class="grid gap-4">
        <div class="font-bold">All Events</div>
        <table class="debug-table">
          <tbody>
            <tr>
              <th class="w-12">ID</th>
              <th class="w-1/4">Restaurant</th>
              <th class="w-12">Rank</th>
              <th class="w-12">Promoted</th>
              <th class="w-12">Meal ID</th>
              <th class="w-1/4">Name</th>
              <th class="w-1/4">Desc</th>
              <th class="w-12">Type</th>
              <th class="w-12">AltType</th>
            </tr>
            <tr v-for="meal in mealsData?.data" :key="meal.id">
              <td v-html="meal.restaurant?.id" />
              <td v-html="meal.restaurant?.name" />
              <td v-html="meal.restaurant?.rank" />
              <td>
                <div class="whitespace-nowrap" v-if="meal.restaurant?.promoted && meal.restaurant?.promoted?.promotionTypes?.includes('events')">
                  {{ meal.restaurant?.promoted?.text }} ({{ meal.restaurant?.promoted?.promotionTypes.join(',') }}, {{ meal.restaurant?.promoted?.rank }})
                  <br>
                  {{ meal.restaurant?.promoted?.startDate }} {{ meal.restaurant?.promoted?.endDate }}
                </div>
              </td>
              <td v-html="meal.id" />
              <td v-html="meal.name" />
              <td v-html="meal.description?.length > 10 ? meal.description.slice(0, 8) + '...' : meal.description" />
              <td v-html="meal.type" />
              <td v-html="meal.altType" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <NuxtLink class="flex items-center gap-2" :to="`/${$t('routes.events')}/${fat.city?.slug}`">
      <div class="w-5 h-5 flex items-center justify-center rounded-full bg-sky text-ocean">
        <AtomIcon name="chevron-right" class="text-sm" />
      </div>
      <div class="text-sm">{{ $t('shared.allEvents', { city: fat.city?.name }) }}</div>
    </NuxtLink>
  </div>
</template>

<script setup>
const { t } = useI18n()
const route = useRoute()
const fat = useFatStore()

const date = computed(() => {
  const now = toDayjs()
  const cutoffHour = 21

  return now.hour() < cutoffHour
    ? now.format('YYYY-MM-DD')
    : now.add(1, 'day').format('YYYY-MM-DD')
})

const startDate = computed(() => date.value)
const endDate = computed(() => toDayjs(date.value).add(14, 'days').format('YYYY-MM-DD'))

// Fetch events
const { data: mealsData, status } = await useFetch('/events', {
  key: `events-${fat.city?.id}`,
  baseURL: useEnv('apiBase'),
  query: {
    scope: 'bokabord.se',
    bookable: true,
    mealTypes: fat.defaultMealTypes.join(','),
    limit: 120,
    cities: computed(() => fat.cityIds?.join(',')),
    startDate: startDate.value,
    endDate: endDate.value,
    maxDays: 8,
  }
})

// Filter meals to contain upcoming 3 events
const curatedMeals = computed(() => {
  if (status.value === 'pending') return []

  let meals = mealsData?.value?.data
    .filter(meal => meal.name.length > 2)

  meals.forEach(meal => {
    meal.restaurantId = meal.restaurant?.id
    meal.startDate = toDayjs().isAfter(meal.startDate) ? toDayjs().format('YYYY-MM-DD') : meal.nextDates[0]
  })

  const uniqueMeals = useUniqueBy(meals, 'restaurantId')

  const sortedMeals = uniqueMeals.sort((a, b) => {
    const dateA = new Date(a.nextDates[0])
    const dateB = new Date(b.nextDates[0])

    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime() // Sort by date ascending
    }

    const rankA = a.restaurant?.rank ?? 0
    const rankB = b.restaurant?.rank ?? 0

    return rankB - rankA // Sort by rank descending if date is the same
  })

  return sortedMeals.slice(0, 3)
})

// Check heading suffix label (tonight, tomorrow, this week, next week)
const headingSuffix = computed(() => {
  const meals = curatedMeals.value
  if (!meals.length) return ''

  const today = toDayjs()
  const tomorrow = today.add(1, 'day')

  const firstDate = toDayjs(meals[0]?.startDate)
  const lastDate = toDayjs(meals[meals?.length - 1]?.startDate)

  // Check if all meals are today
  if (firstDate.isSame(today, 'day') && lastDate.isSame(today, 'day')) {
    return t('shared.tonight')
  }

  // Check if all meals are tomorrow
  if (firstDate.isSame(tomorrow, 'day') && lastDate.isSame(tomorrow, 'day')) {
    return t('shared.tomorrow')
  }

  const startOfThisWeek = today.startOf('week')
  const endOfThisWeek = today.endOf('week')

  // Check if all meals are within this week
  if (firstDate.isSameOrAfter(today, 'day') && lastDate.isSameOrBefore(endOfThisWeek, 'day')) {
    return t('shared.thisWeek')
  }

  const startOfNextWeek = startOfThisWeek.add(1, 'week')
  const endOfNextWeek = endOfThisWeek.add(1, 'week')

  // Check if all meals fall within next week
  if (firstDate.isSameOrAfter(startOfNextWeek, 'day') && lastDate.isSameOrBefore(endOfNextWeek, 'day')) {
    return t('shared.nextWeek')
  }

  // Fallback to first meal’s weekday + date
  return ''
})
</script>